'use client'

import * as React from 'react'
import * as ScrollAreaPrimitive from '@radix-ui/react-scroll-area'
import { clsx } from 'clsx'

const ScrollArea = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.Root> & {
    orientation?: 'vertical' | 'horizontal' | 'both'
    viewportClassName?: string
    forceDisplayBlock?: boolean
    scrollBarClassName?: string
    variant?: 'default' | 'subtle'
    viewportStyle?: React.CSSProperties
    viewportRef?: React.RefObject<null | HTMLDivElement>
    viewportOnScroll?: (e: React.UIEvent<HTMLDivElement>) => void
  }
>(
  (
    {
      className,
      children,
      orientation,
      viewportClassName,
      scrollBarClassName,
      forceDisplayBlock = true,
      asChild,
      variant = 'default',
      viewportStyle,
      viewportRef,
      viewportOnScroll,
      ...props
    },
    ref
  ) => (
    <ScrollAreaPrimitive.Root
      ref={ref}
      className={clsx('relative overflow-hidden', className)}
      {...props}
    >
      <ScrollAreaPrimitive.Viewport
        className={clsx(
          'h-full w-full',
          forceDisplayBlock && '[&>div]:!block',
          orientation === 'horizontal' && '[&>div]:w-max',
          orientation === 'vertical' && '[&>div]:h-max',
          orientation === 'both' && '[&>div]:h-max [&>div]:w-max',
          viewportClassName
        )}
        style={viewportStyle}
        asChild={asChild}
        ref={viewportRef}
        onScroll={viewportOnScroll}
      >
        {children}
      </ScrollAreaPrimitive.Viewport>
      <ScrollBar
        orientation={orientation === 'both' ? 'vertical' : orientation}
        className={scrollBarClassName}
        variant={variant}
      />
      {orientation === 'both' && (
        <ScrollBar
          orientation="horizontal"
          className={scrollBarClassName}
          variant={variant}
        />
      )}
      <ScrollAreaPrimitive.Corner className="bg-dark-bg" />
    </ScrollAreaPrimitive.Root>
  )
)
ScrollArea.displayName = ScrollAreaPrimitive.Root.displayName

const ScrollBar = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>,
  React.ComponentPropsWithoutRef<
    typeof ScrollAreaPrimitive.ScrollAreaScrollbar
  > & {
    variant?: 'default' | 'subtle'
  }
>(
  (
    { className, orientation = 'vertical', variant = 'default', ...props },
    ref
  ) => (
    <ScrollAreaPrimitive.ScrollAreaScrollbar
      ref={ref}
      orientation={orientation}
      className={clsx(
        'group z-scrollbar',
        variant === 'subtle' && '[--size:6px] [--padding:1px]',
        variant === 'default' && '[--size:10px] [--padding:2px]',
        'flex touch-none select-none transition-colors',
        orientation === 'vertical' &&
          'h-full w-[var(--size)] border-l border-l-transparent p-[var(--padding)]',
        orientation === 'horizontal' &&
          'h-[var(--size)] border-t border-t-transparent p-[var(--padding)]',
        className
      )}
      {...props}
    >
      <ScrollAreaPrimitive.ScrollAreaThumb className="relative flex-1 rounded-full bg-dark-border-solid group-hover:bg-dark-elevated max-w-[var(--radix-scroll-area-thumb-width)] max-h-[var(--radix-scroll-area-thumb-height)]" />
    </ScrollAreaPrimitive.ScrollAreaScrollbar>
  )
)
ScrollBar.displayName = ScrollAreaPrimitive.ScrollAreaScrollbar.displayName

export { ScrollArea, ScrollBar }
